<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="6" v-if="screenGrabId && screenGrabId !== 'missing'">
        <div class="d-flex justify-center">
          <v-btn v-if="option.url"
            text
            dark
            x-small
            target="_blank"
            :href="option.url"
          >
            <span v-if="optionHostname" class="accent--text">{{optionHostname}}</span><v-icon class="ml-1" color="accent" x-small>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
        <div class="mr-3 d-none d-md-block" v-if="screenShotReady">
          <MagnifiedScreenshotImage :img-url="screenShotSrc" :zoom="3"></MagnifiedScreenshotImage>
        </div>
        <div class="mr-3 d-none d-md-block" v-else>
          <ScreenshotSkeletonLoader :screenShotReady="screenShotReady" @onReady="handleScreenShotReady()" :optionUrl="option.url" :screenGrabId="screenGrabId"></ScreenshotSkeletonLoader>
        </div>
      </v-col>
      <v-col cols="12" md="6">
        <v-card :class="[displayCardClass]" outlined v-scroll.self="onCardScroll">
         <v-divider></v-divider>
         <v-carousel
            v-if="titleImages.length > 1"
            v-model="imageIndex"
            :hide-delimiters="titleImages.length > 10"
            height="200"
          >
            <v-carousel-item
              v-for="(nextImageSrc, i) in titleImages"
              :key="i"
            >
              <v-sheet
                height="100%"
              >
                <v-row
                  class="fill-height"
                  align="center"
                  justify="center"
                >
                  <v-img
                    :src="getNextOptionImg(nextImageSrc)"
                    class="white--text align-end"
                    contain
                    height="200px"
                  >
                   <v-card-title v-text="option.name"></v-card-title>
                  </v-img>
                </v-row>
              </v-sheet>
            </v-carousel-item>
          </v-carousel>
         <v-img
            v-else
           :src="optionImgSrc"
           class="white--text align-end"
           contain
           :gradient="imgUrl ? undefined : 'to bottom, rgba(0,0,0,.5), rgba(0,0,0,.9)'"
           height="200px"
         >
          <div style="display: flex; justify-content: center;">
            <v-btn rounded v-if="!imgUrl">
              Upload an image
            </v-btn>
          </div>
          <v-card-title v-text="option.name"></v-card-title>
         </v-img>
         <v-card-actions v-if="showTopActions">

           <slot name="leftActions"></slot>

           <v-spacer></v-spacer>

           <slot name="rightActions"></slot>

         </v-card-actions>
         <v-container fluid class="py-0">
           <div v-for="criteriaItem in criteria" :key="criteriaItem.id">
             <v-row>
               <v-col cols="12" align="start" class="pb-0">
                 {{criteriaItem.name}}:
               </v-col>
               <v-col cols="12" align="start" class="pt-0">
                 <CriteriaValue v-if="option.criteriaValues" :keywordOnly="criteriaItem.keywordOnly" :value="option.criteriaValues[criteriaItem.id]" :showValueChanges="hasPreviousValues" :lastValue="previousCriteriaValues ? previousCriteriaValues[criteriaItem.id] : null">
                   <template v-slot:options v-if="criteriaValueOptions">
                     <div v-if="criteriaValueOptions[criteriaItem.id] && criteriaValueOptions[criteriaItem.id].length > 0">
                       <div>
                         <div class="d-flex align-center">
                           <div class="info--text my-1">
                             Choose a value
                           </div>
                         </div>
                         <v-chip-group
                           mandatory
                           column
                           active-class="primary"
                         >
                           <v-chip @click="selectSourceValue(criteriaItem.id, criteriaValue, valueIndex)" class="source-value-chip" :title="criteriaValue.value" v-for="(criteriaValue, valueIndex) in criteriaValueOptions[criteriaItem.id]" :key="valueIndex" :value="criteriaValue.value">
                             <v-icon v-if="criteriaItem.keywordOnly" class="mr-1" x-small>mdi-check</v-icon>
                             {{ criteriaValue.value }}
                           </v-chip>
                         </v-chip-group>
                       </div>
                       <v-select
                         v-if="criteriaItem.keywordOnly && !option.criteriaValues[criteriaItem.id].sourceValue"
                         v-model="option.criteriaValues[criteriaItem.id].userValue"
                         outlined
                         :dense="true"
                         :items="booleanCriteriaValueOptions"
                         :placeholder="criteriaValueOptions[criteriaItem.id] && criteriaValueOptions[criteriaItem.id].length > 0 ? 'Or, enter your own value' : 'Provide a value'"
                       ></v-select>
                       <v-text-field
                         v-if="!criteriaItem.keywordOnly"
                         single-line
                         outlined
                         :dense="true"
                         hide-details="auto"
                         v-model="option.criteriaValues[criteriaItem.id].userValue"
                         :placeholder="criteriaValueOptions[criteriaItem.id] && criteriaValueOptions[criteriaItem.id].length > 0 ? 'Or, enter your own value' : 'Provide a value'"
                       ></v-text-field>
                     </div>
                     <div v-else class="info--text text-caption">
                       No changes found
                     </div>
                   </template>
                 </CriteriaValue>
               </v-col>
             </v-row>
          </div>
         </v-container>
         <v-card-actions v-if="!showTopActions">

           <slot name="leftActions"></slot>

           <v-spacer></v-spacer>

           <slot name="rightActions"></slot>

         </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Constants from '../constants.json'
// Custom mixins
import guid from '@/mixins/DecidedlyGUID.js'

import CriteriaValue from '@/components/CriteriaValue.vue'
import MagnifiedScreenshotImage from '@/components/MagnifiedScreenshotImage.vue'
import ScreenshotSkeletonLoader from '@/components/ScreenshotSkeletonLoader.vue'

export default {
  name: 'OptionReviewCard',
  mixins: [guid],
  components: {
    CriteriaValue,
    MagnifiedScreenshotImage,
    ScreenshotSkeletonLoader
  },
  props: {
    isFixed: {
      type: Boolean,
      default: true
    },
    option: Object,
    imgUrl: String,
    imageOptions: Array,
    criteria: Array,
    criteriaValueOptions: Object,
    previousCriteriaValues: Object,
    screenGrabId: String,
    screenShotReady: Boolean
  },
  computed: {
    displayCardClass () {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs': return 'rounded-0'
        case 'sm': return 'rounded-0'
        case 'md': return 'rounded-0 fixed-card'
        case 'lg': return 'rounded-0 fixed-card'
        case 'xl': return 'rounded-0 fixed-card'
        default: return 'rounded-0'
      }
    },
    screenShotSrc () {
      let res = null
      if (this.screenGrabId && this.screenGrabId !== 'missing') {
        const randomQS = this.genGUID()
        res = `${Constants.SCREENSHOT_CDN}/${this.screenGrabId}.png?${randomQS}`
      }
      return res
    },
    optionImgSrc () {
      let res = this.imgUrl || `${Constants.SCREENSHOT_CDN}/unknownimage.png`
      // if (res.indexOf('https:') !== 0) {
      //    res = `${Constants.SCREENSHOT_CDN}/notsecureimage.png`
      // }
      return res
    },
    titleImages () {
      let res = []
      if (this.imageOptions) {
        res = this.imageOptions
      }
      return res
    },
    imageIndex: {
      // getter
      get: function () {
        return this.titleImages.indexOf(this.imgUrl)
      },
      // setter
      set: function (newIndex) {
        this.$set(this.option, 'imgSrc', this.titleImages[newIndex])
        // this.option.imgSrc = this.titleImages[newIndex]
      }
    },
    optionHostname () {
      if (this.option.url) {
        return new URL(this.option.url).hostname
      } else {
        return null
      }
    },
    hasPreviousValues () {
      let res = false
      if (this.previousCriteriaValues) {
        res = true
      }

      return res
    }
  },
  data () {
    return {
      booleanCriteriaValueOptions: ['YES', 'NO'],
      showTopActions: true
    }
  },
  mounted: function () {
    // this.updateCriteriaValues()
  },
  methods: {
    openURL: function (url) {
      window.open(url, '_blank');
    },
    getNextOptionImg: function (imgUrl) {
      let res = imgUrl || `${Constants.SCREENSHOT_CDN}/unknownimage.png`
      // if (res && res.indexOf('https:') !== 0) {
      //    res = `${Constants.SCREENSHOT_CDN}/notsecureimage.png`
      // }
      return res
    },
    selectSourceValue: function (criteriaId, criteriaValue, valueIndex) {
      this.$emit('onSelectValue', criteriaId, criteriaValue, valueIndex)
      //this.option.criteriaValues[criteriaId].sourceValue = criteriaValue.value
      //this.option.criteriaValues[criteriaId].selectorId = criteriaValue.selectorId
      //this.option.criteriaValues[criteriaId].selectorKey = criteriaValue.selectorKey
      //this.option.criteriaValues[criteriaId].sourceIndex = valueIndex
    },
    handleScreenShotReady: function () {
      this.$emit('update:screenShotReady', true)
    },
    updateCriteriaValues: function () {
      this.criteria.forEach((item) => {
        if (!this.option.criteriaValues[item.id]) {
          this.option.criteriaValues[item.id] = {userValue: null, sourceValue: null}
        }
      })
    },
    onCardScroll: function (e) {
      const offsetTop = e.target.scrollTop
      if (offsetTop > 60) {
        this.showTopActions = false
      } else if (!this.showTopActions) {
        this.showTopActions = true
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
.review-container {
  position: relative;
}

.fixed-card {
  position: fixed;
  max-width: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 85%;
}

.v-card__title {
  font-size: 1rem;
  position: relative;
  background-color: rgba(0,0,0,0.6);
  padding: 5px 16px;
}

</style>
